import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Breakpoint, Typography } from 'app/component-library-wave';
import { Button, RadioGroup, RadioGroupItem, Spinner } from '@lyse-as/formds-react';

import { ContainerFixed } from 'app/components/container';
import { CustomerPage } from 'app/pages/page-wrapper';
import { useAppDispatch, useAppSelector } from 'app/hooks/redux-thunk';
import { FetchStatus } from 'app/store/root-types';
import { fetchConsent, updateConsents } from 'app/store/actions/consent-thunks';
import { getDataLayers } from 'app/utils/trackingService';
import { getAnonymizedCurrentPageUrl, GtmElementCta, GtmFormName } from 'app/utils/gtm-tracking-utils';

import styles from './reservations.module.scss';

export const Reservations: CustomerPage = (props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const consent = useAppSelector((state) => state.consent.consent);
  const fetchStatus = useAppSelector((state) => state.consent.fetchStatus);
  const [selectedConsent, setSelectedConsent] = useState<'yes' | 'no' | undefined>();

  useEffect(() => {
    switch (consent?.simpleProfiling) {
      case undefined:
        setSelectedConsent(undefined);
        break;
      case true:
        setSelectedConsent('yes');
        break;
      default:
        setSelectedConsent('no');
        break;
    }
  }, [consent]);

  useEffect(() => {
    if (fetchStatus === FetchStatus.NOT_STARTED) {
      dispatch(fetchConsent());
    }
  }, [dispatch, fetchStatus]);

  const onSave = () => {
    if (!consent || selectedConsent === undefined) {
      // eslint-disable-next-line no-console
      console.warn('Consent store values missing.');
      return;
    }

    const newConsents = { ...consent, simpleProfiling: selectedConsent === 'yes' };

    getDataLayers().customEvents.pushFormSubmissionEvent({
      element_cta: GtmElementCta.PRIVACY_AND_CONCENTS_RESERVATIONS_SAVE,
      page_url: getAnonymizedCurrentPageUrl(),
      form_name: GtmFormName.RESERVASIONS,
      form_values: newConsents,
    });

    dispatch(updateConsents(newConsents));
  };

  return (
    <ContainerFixed isNarrow={true}>
      <div className={styles.pageContainer}>
        <Typography variant="headline4" component="h1" maxBreakpoint={Breakpoint.TABLET} className={styles.heading}>
          {t('pages.consent.reservations.heading')}
        </Typography>

        <Typography variant="paragraph2" component="p" maxBreakpoint={Breakpoint.MOBILE} className={styles.lead}>
          {t('pages.consent.reservations.lead', { partnerName: props.userContext.partnerLegalName })}
        </Typography>

        <div className={styles.card}>
          <div className={styles.cardDescription}>
            <Trans
              i18nKey={'pages.consent.reservations.description'}
              values={{ partnerName: props.userContext.partnerLegalName }}
            >
              <strong />
              <strong />
            </Trans>
          </div>

          <RadioGroup
            orientation="vertical"
            disabled={fetchStatus === FetchStatus.PENDING}
            defaultValue={selectedConsent}
            onValueChange={(value: string) => setSelectedConsent(value as 'yes' | 'no')}
          >
            <RadioGroupItem value={'yes'}>{t('pages.consent.reservations.deny')}</RadioGroupItem>
            <RadioGroupItem value={'no'}>{t('pages.consent.reservations.permit')}</RadioGroupItem>
          </RadioGroup>
        </div>

        <Button
          variant="filled"
          scheme="primary"
          onClick={onSave}
          disabled={fetchStatus === FetchStatus.PENDING || selectedConsent === undefined}
        >
          {fetchStatus === FetchStatus.PENDING ? (
            <span style={{ display: 'inline-flex', width: '1.25em', height: '1.25em' }}>
              <Spinner loadingText={'Laster'} loadingCompleteText={'Ferdig'} isLoadingComplete={false} />
            </span>
          ) : (
            <>{t('pages.consent.ctaSave')}</>
          )}
        </Button>
      </div>
    </ContainerFixed>
  );
};
